import React, { useRef, useState, useEffect } from 'react';
import { Play, ArrowRight, Volume2, VolumeX } from 'lucide-react';
import { showreel, bgVideoId } from '../mock';
import { track } from '../lib/analytics';

const Hero = ({ onPlay }) => {
  const iframeRef = useRef(null);
  const [audioOn, setAudioOn] = useState(false);
  const [videoOk, setVideoOk] = useState(false); // becomes true only when the reel actually plays
  const [videoEnabled, setVideoEnabled] = useState(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = navigator.connection?.saveData;
    if (reducedMotion || saveData) return undefined;
    const enable = () => setVideoEnabled(true);
    const timer = window.requestIdleCallback
      ? window.requestIdleCallback(enable, { timeout: 1800 })
      : window.setTimeout(enable, 1200);
    return () => window.cancelIdleCallback ? window.cancelIdleCallback(timer) : window.clearTimeout(timer);
  }, []);

  const post = (func, args = []) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      '*'
    );
  };

  // Listen to the YouTube player for playback / error events.
  useEffect(() => {
    const onMsg = (e) => {
      if (typeof e.data !== 'string' || !e.origin.includes('youtube')) return;
      try {
        const data = JSON.parse(e.data);
        if (data.event === 'onError') setVideoOk(false);
        if (data.event === 'onStateChange' && data.info === 1) setVideoOk(true);
        if (data.event === 'infoDelivery' && data.info && data.info.playerState === 1) setVideoOk(true);
        // Loop via the API (avoids YouTube's playlist prev/next chrome).
        const ended = data.info === 0 || (data.info && data.info.playerState === 0);
        if ((data.event === 'onStateChange' || data.event === 'infoDelivery') && ended) {
          post('seekTo', [0, true]);
          post('playVideo');
        }
      } catch (_) { /* ignore */ }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const handleLoad = () => {
    const win = iframeRef.current?.contentWindow;
    // handshake so the player starts posting events
    win?.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*');
    [400, 1200, 2500, 4000].forEach((t) => setTimeout(() => post('playVideo'), t));
  };

  const toggleAudio = () => {
    if (audioOn) {
      post('mute');
      setAudioOn(false);
    } else {
      setVideoEnabled(true);
      setVideoOk(true);
      [100, 400, 1000, 1800].forEach((t) => setTimeout(() => { post('unMute'); post('playVideo'); }, t));
      setAudioOn(true);
    }
  };

  const bgSrc =
    `https://www.youtube.com/embed/${bgVideoId}?autoplay=1&mute=1&controls=0` +
    `&playsinline=1&modestbranding=1&rel=0&showinfo=0` +
    `&iv_load_policy=3&disablekb=1&fs=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <section id="top" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Cinematic poster (always present; video covers it when it plays) */}
      <div
        className="absolute inset-0 bg-cover bg-center hero-poster"
        style={{ backgroundImage: "url('/media/commercial.jpg')" }}
      />

      {/* Background video (hidden until confirmed playing to avoid error cards) */}
      <div className={`video-bg transition-opacity duration-700 ${videoOk ? 'opacity-100' : 'opacity-0'}`}>
        {videoEnabled && (
          <iframe
            ref={iframeRef}
            src={bgSrc}
            title="Simply Gold background reel"
            allow="autoplay; encrypted-media"
            loading="lazy"
            onLoad={handleLoad}
          />
        )}
      </div>

      {/* Readability overlays */}
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/25 to-black/75" />
      <div className="absolute inset-0 hero-glow" />

      {/* Tap guard — swallows touches so YouTube's mobile controls/logo never appear */}
      <div className="absolute inset-0 z-[5]" aria-hidden="true" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24">
        <p className="font-display tracking-[0.4em] text-[#c9a85f] text-sm md:text-base uppercase mb-6">
          Kelowna &middot; Vancouver Video Production
        </p>
        <h1 className="font-display font-bold text-white text-4xl md:text-6xl lg:text-7xl leading-[0.98] uppercase drop-shadow-[0_2px_20px_rgba(0,0,0,0.55)]">
          Documentary Storytelling For Companies Doing
          <span className="block text-[#c9a85f]">Extraordinary Work</span>
        </h1>
        <p className="mt-7 text-white/90 text-lg md:text-2xl max-w-3xl mx-auto font-light leading-snug">
          We uncover authentic stories that create trust, move people and help businesses grow.
        </p>

        {/* Play Audio toggle for the background reel */}
        <button
          onClick={toggleAudio}
          data-testid="hero-play-audio"
          className="group mt-7 inline-flex items-center gap-2.5 text-[#c9a85f] hover:text-white font-display tracking-widest uppercase text-xs md:text-sm transition-colors"
        >
          <span className="flex items-center justify-center w-8 h-8 rounded-full border border-[#c9a85f]/60 group-hover:border-white group-hover:bg-white/10 transition-colors">
            {audioOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </span>
          {audioOn ? 'Mute Audio' : 'Play Audio'}
        </button>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => { track('showreel_opened', { location: 'hero' }); onPlay(showreel.youtubeId, showreel.title); }}
            className="group inline-flex items-center gap-3 bg-[#c9a85f] hover:bg-[#d8b970] text-black font-display font-semibold px-7 py-3.5 rounded-md transition-colors tracking-wide"
          >
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-black/15 group-hover:bg-black/25 transition-colors">
              <Play size={13} className="fill-black text-black ml-0.5" />
            </span>
            Watch Showreel
          </button>
          <a
            href="#brand-stories"
            onClick={(e) => { e.preventDefault(); document.querySelector('#brand-stories')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="group inline-flex items-center gap-2 text-white/90 hover:text-white font-display font-medium px-4 py-3.5 tracking-wide"
          >
            View Our Work
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 z-10">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#c9a85f] to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
